import React, { Fragment, useCallback, useContext, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import MediaDialog from "../../common/MediaDialog.js";

import Grid from "@material-ui/core/Grid";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";

import {
  validateAnthology,
  validateAnthologyField,
  hasError,
  firstError,
} from "../anthologyValidation";
import EditColumn from "../../common/EditColumn";
import NewThumbnailOverlay from "../../common/NewThumbnailOverlay";
import EditDescriptionColumn from "../../common/EditDescriptionColumn.js";
import EditCategoriesColumn from "../../common/EditCategoriesColumn.js";
import { useSnackAlert } from "../../../common/use-snackAlert";
import {
  useAnthologyCreate,
  useUploadAnthologyThumbnail,
  useUploadAnthologyOrganizationLogo,
} from "../anthologyServices";
import NewOrgLogoOverlay from "../../common/NewOrgLogoOverlay";
import { useRouteMatch } from "react-router-dom";
import { MediaContext } from "../../common/media-context";
import { isBlank, notBlank } from "../../../util/ext";
import { mergeRight } from "ramda";
import LoadingIndicator from "../../../components/LoadingIndicator.js";

const useStyles = makeStyles(theme => ({
  root: {
    padding: "0",
  },

  dialog: {
    width: "866px",
    borderRadius: "20px",
    boxShadow: "0px 0px 20px rgba(0, 0, 0, 0.15)",
  },

  close: {
    width: "40px",
    height: "40px",
    position: "relative",
    right: "-10px",
  },

  error: {
    color: theme.palette.error.main,
    paddingTop: 5,
  },

  title: {
    textAlign: "left",
    color: theme.palette.brandPrimary,
  },

  loadingIndicator: {
    position: "absolute",
    height: "100%",
    width: "100%",
    backgroundColor: theme.palette.secondary.dark,
    opacity: 0.7,
  },
}));

const validator = {
  validateField: validateAnthologyField,
  validate: validateAnthology,
  hasError: hasError,
  firstError: firstError,
};

const initialAnthology = {
  name: "",
  subName: "",
  mediaType: "anthology",
  description: "",
  descriptionText: "",
  bisacCategories: [],
};

const AddNewAnthologyDialog = props => {
  const classes = useStyles();
  const { path } = useRouteMatch();
  const context = useContext(MediaContext);
  const { open, onSave, onClose, isCollection } = props;

  const { SnackAlert, showAlert } = useSnackAlert();

  //-- Set up overall state
  const [anthology, setAnthology] = useState(initialAnthology);
  const [thumbnailUrl, setThumbnailUrl] = useState(null);
  const [thumbnailFile, setThumbnailFile] = useState(null);
  const [orgLogoUrl, setOrgLogoUrl] = useState(null);
  const [orgLogoFile, setOrgLogoFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const uploadThumbnail = useUploadAnthologyThumbnail();
  const uploadOrgLogo = useUploadAnthologyOrganizationLogo();

  const createAnthology = useAnthologyCreate();
  const handleSave = useCallback(async () => {
    setLoading(true);
    const validation = mergeRight(validator.validate(anthology), errors);

    try {
      if (notBlank(validation || isBlank(thumbnailFile))) {
        if (isBlank(thumbnailFile)) {
          setErrors(
            mergeRight(validation, {
              thumbnail: ["A thumbnail must be selected"],
            }),
          );
        } else {
          setErrors(validation);
        }

        throw new Error("Please address missing or invalid values");
      }
      // Create new anthology
      const newAnthology = await createAnthology.mutateAsync({
        data: anthology,
      });

      // Upload thumbnail
      await uploadThumbnail.mutateAsync({
        resourceIds: { id: newAnthology.id },
        data: thumbnailFile,
      });

      if (notBlank(orgLogoFile)) {
        await uploadOrgLogo.mutateAsync({
          resourceIds: { id: newAnthology.id },
          data: orgLogoFile,
        });
      }

      // Redirect to new anthology edit screen
      setLoading(false);
      onSave({
        redirectPath: path,
        newAnthologyId: newAnthology.id,
      });
    } catch (error) {
      showAlert("error", [error.message]);
    } finally {
      setLoading(false);
    }
  }, [
    anthology,
    thumbnailFile,
    createAnthology,
    uploadThumbnail,
    errors,
    showAlert,
    onSave,
    orgLogoFile,
    path,
    uploadOrgLogo,
  ]);

  const handleClose = useCallback(() => {
    setAnthology(initialAnthology);
    setThumbnailUrl(null);
    setThumbnailFile(null);
    setOrgLogoUrl(null);
    setOrgLogoFile(null);
    setErrors({});
    setLoading(false);
    onClose();
  }, [onClose, setLoading]);

  const formBuilder = useCallback(
    handlers => {
      return (
        <MediaDialog
          open={open}
          fullWidth
          maxWidth="md"
          title={`Add New ${isCollection ? "Collection" : "Anthology"}`}
          handleClose={handleClose}
          loading={loading}
          content={
            <Grid container>
              {loading && <LoadingIndicator transparent />}
              <Grid item style={{ marginRight: "16px" }}>
                <NewThumbnailOverlay
                  accept="jpg, jpeg, image/jpeg, image/jpg, png, image/png"
                  showAlert={showAlert}
                />
                {validator.hasError(errors, "thumbnail") && (
                  <div className={classes.error}>
                    {validator.firstError(errors, "thumbnail")}
                  </div>
                )}
              </Grid>
              <Grid container item xs>
                <TextField
                  id="anthology-name"
                  fullWidth
                  required
                  label="Title"
                  value={context.media.name}
                  onChange={handlers.onTextChange(["name"])}
                  error={hasError(errors, "name")}
                  helperText={firstError(errors, "name")}
                  onBlur={() => {
                    setErrors(validateAnthologyField("name", anthology.name));
                  }}
                />

                <TextField
                  id="collection-sub-name"
                  fullWidth
                  label="Subtitle"
                  value={context.media.name}
                  onChange={handlers.onTextChange(["subName"])}
                  error={hasError(errors, "subName")}
                  helperText={firstError(errors, "subName")}
                  onBlur={() => {
                    setErrors(
                      validateAnthologyField("subName", anthology.subName),
                    );
                  }}
                />
              </Grid>

              <Grid item xs={12} style={{ marginTop: 45 }}>
                <EditDescriptionColumn
                  required
                  enableLinks
                  allowHtml
                  error={validator.hasError(errors, "description")}
                  helperText={validator.firstError(errors, "description")}
                  onBlur={() =>
                    handlers.onBlur("description", anthology.description)
                  }
                />
              </Grid>

              <Grid item xs>
                <EditCategoriesColumn
                  showFourCategories
                  validator={validator}
                />
              </Grid>

              <Grid item xs="auto">
                <NewOrgLogoOverlay />
              </Grid>
            </Grid>
          }
          actions={
            <Button
              variant="contained"
              color="primary"
              onClick={handleSave}
              disabled={loading}
            >
              Save
            </Button>
          }
        />
      );
    },
    [
      classes,
      handleClose,
      handleSave,
      showAlert,
      context,
      open,
      anthology,
      loading,
      errors,
      isCollection,
    ],
  );

  return (
    <MediaContext.Provider
      value={{
        initial: initialAnthology,
        media: anthology,
        setMedia: setAnthology,
        errors: errors,
        setErrors: setErrors,
        thumbnailUrl: thumbnailUrl,
        setThumbnailUrl: setThumbnailUrl,
        thumbnailFile: thumbnailFile,
        setThumbnailFile: setThumbnailFile,
        orgLogoUrl,
        setOrgLogoUrl,
        orgLogoFile,
        setOrgLogoFile,
      }}
    >
      <Fragment>
        <SnackAlert />
        <EditColumn
          isNew
          className={classes.root}
          formBuilder={formBuilder}
          validator={validator}
        />
      </Fragment>
    </MediaContext.Provider>
  );
};

export default AddNewAnthologyDialog;
